import { useMemo, useState, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, IconButton, Tooltip, Stack, alpha, useTheme,
  TablePagination, Skeleton, Paper, Switch,
} from '@mui/material';
import { Edit, Delete, Inbox, Visibility, ImageOutlined } from '@mui/icons-material';
import type { MasterSchema, MasterFieldDef } from '../../constants/masters';
import { getMasterTypeForField, normalizeMetadataFields } from '../../constants/masterRefFields';
import { parseCommaSeparatedMasterCodes, resolveMasterRecordActive, resolveTransportMasterImageUrl, formatAvailabilityDaysOfWeek, VEHICLE_FLEET_MASTERS, isMasterRecordDeprecated, RIDE_TAG_COLOR_PATTERN } from '../../constants/transportSubserviceMasters';
import { formatFoodUnitTypeLabel, formatKitchenStationTypeLabel, formatParentCategoryRefLabel, formatTaxPercentDisplay, isParentCategoryRefField, resolveFoodTaxGroupDisplayName, resolveRecordSortOrder } from '../../constants/foodMasterRules';
import { formatPackagingFeeDisplay, formatDeliveryIncentiveAmountDisplay, formatDeliveryIncentivePeakWindow, formatDeliveryPartnerPricingServiceLabel, formatDeliveryPartnerTierBonusDisplay, formatDeliveryPartnerTierMinOrdersDisplay, formatMerchantDocumentCategory, formatMerchantDocumentComplianceRules, formatMerchantDocumentEntityTypes, formatMerchantDocumentRuleFlag, formatCancellationReasonAppliesTo, formatSubstitutionAllowDisplay, resolveCancellationReasonAppliesTo, isDeliverySlotTimeRangeInvalid, isGroceryAvailabilityTimeRangeInvalid, resolvePackagingImageSources, resolvePackagingListImageUrl, resolveGroceryCategoryImageSources, resolveGroceryCategoryListImageUrl, resolveGrocerySubCategoryImageSources, resolveGrocerySubCategoryListImageUrl, resolveGroceryProductImageSources, resolveGroceryProductListImageUrl } from '../../constants/deliveryMasterRules';
import { getCountryLocale } from '../../constants/countryDefaults';
import { countryDisplayName, countryFlag } from '../../utils/stateMasterValidation';
import { formatCurrencyAmount, formatFromMetadata } from '../../utils/currencyMasterValidation';
import { TRANSLATION_STATUS_META, translationStatusOf, type LanguageTranslationStatus } from '../../utils/languageMasterValidation';
import { ADDRESS_FIELD_SOURCE_LABELS, ADDRESS_FIELD_TYPE_LABELS, ADDRESS_GEO_LEVEL_LABELS } from '../../utils/addressFieldMasterValidation';
import { PAYMENT_FEE_BASIS_LABELS } from '../../utils/paymentMethodMasterValidation';
import { getMasterOptions } from '../../services/platform/masterService';
import { useZoneCodeMap } from './ZoneMasterSelect';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import { resolveAssetUrl } from '../../utils/assetUrl';
import {
  resolveFoodCategoryListImageUrl,
  resolveCuisineListImageUrl,
  resolveFoodAddonImageSources,
  resolveFoodImageListImageUrl,
  resolveFoodImageSources,
} from '../../utils/foodMasterImages';
import { chargeFieldLabel, formatMoney } from '../../utils/currency';
import { resolveDietaryListImageUrl } from '../../utils/dietaryMasterIcons';
import {
  formatOpeningHourDayLabel,
  formatOpeningHourTimeRange,
  isOpeningHourRowInvalid,
  findDuplicateOpeningHourDays,
} from '../../utils/restaurantOpeningHours';

export interface MasterRecordRow {
  _id: string;
  code: string;
  name: string;
  description?: string;
  country?: string;
  state?: string;
  stateName?: string;
  isBase?: boolean;
  baseCurrency?: string;
  isDefault?: boolean;
  translationStatus?: LanguageTranslationStatus;
  currentOffset?: string | null;
  dstActive?: boolean;
  city?: string;
  district?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
  isActive?: boolean;
  toggleLock?: string | null;
  deleteLock?: string | null;
  isCore?: boolean;
  gatewayStatus?: { value: string; label: string; kind: 'online' | 'offline'; ready: boolean; reason: string | null };
  usageCount?: number;
  partnerCount?: number;
}

function formatCountryCell(rawCode: string) {
  const code = rawCode.toUpperCase();
  const known = getCountryLocale(code).label;
  const name = known && known.toUpperCase() !== code ? known : countryDisplayName(code);
  return name && name.toUpperCase() !== code ? `${countryFlag(code)} ${name} (${code})`.trim() : code;
}

const ROUNDING_LABELS: Record<string, string> = { half_up: 'Half up', half_down: 'Half down', bankers: 'Bankers', floor: 'Floor' };

function timezoneCells(row: MasterRecordRow) {
  const meta = row.metadata || {};
  const countries = Array.isArray(meta.countries) ? (meta.countries as string[]) : [];
  const dst = meta.dstSupported === true;
  return [
    <TableCell key="ianaId" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      <Typography variant="caption" fontFamily="monospace">{String(meta.ianaId || '—')}</Typography>
    </TableCell>,
    <TableCell key="offset" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="body2" fontFamily="monospace" noWrap>
        UTC{String(meta.utcOffset || '')}{dst && meta.dstOffset ? ` / ${String(meta.dstOffset)}` : ''}
      </Typography>
      <Chip
        size="small"
        label={dst ? 'DST' : 'No DST'}
        color={dst ? 'info' : 'default'}
        variant="outlined"
        sx={{ height: 18, fontSize: 11 }}
      />
    </TableCell>,
    <TableCell key="now" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
      <Typography variant="body2" fontFamily="monospace" noWrap>{row.currentOffset ? `UTC${row.currentOffset}` : '—'}</Typography>
      {row.dstActive && <Typography variant="caption" color="info.main">Daylight saving</Typography>}
    </TableCell>,
    <TableCell key="abbreviation" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="caption">{String(meta.abbreviation || '—')}</Typography>
    </TableCell>,
    <TableCell key="countries" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
      <Tooltip title={countries.join(', ')}>
        <Typography variant="caption" noWrap sx={{ maxWidth: 120, display: 'block' }}>
          {countries.length ? countries.slice(0, 4).join(', ') + (countries.length > 4 ? ` +${countries.length - 4}` : '') : '—'}
        </Typography>
      </Tooltip>
    </TableCell>,
  ];
}

function addressFieldCells(row: MasterRecordRow) {
  const meta = row.metadata || {};
  const geo = String(meta.geoLevel || '').toLowerCase();
  const geoDef = ADDRESS_GEO_LEVEL_LABELS[geo];
  const parent = geoDef?.parent ? ADDRESS_GEO_LEVEL_LABELS[geoDef.parent]?.label : null;
  const fieldType = String(meta.fieldType || '');
  const source = String(meta.source || '');
  const required = meta.required === true || meta.required === 'true';
  return [
    <TableCell key="fieldKey" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      <Typography variant="caption" fontFamily="monospace">{String(meta.fieldKey || row.code || '—')}</Typography>
    </TableCell>,
    <TableCell key="geoLevel" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="body2" noWrap>{geoDef?.label || geo || '—'}</Typography>
      {parent && <Typography variant="caption" color="text.secondary" noWrap display="block">↳ under {parent}</Typography>}
    </TableCell>,
    <TableCell key="fieldType" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="caption">{ADDRESS_FIELD_TYPE_LABELS[fieldType] || fieldType || '—'}</Typography>
    </TableCell>,
    <TableCell key="source" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
      <Typography variant="caption" color="text.secondary">{ADDRESS_FIELD_SOURCE_LABELS[source] || source || '—'}</Typography>
    </TableCell>,
    <TableCell key="required" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Chip
        size="small"
        label={required ? 'Required' : 'Optional'}
        color={required ? 'primary' : 'default'}
        variant="outlined"
        sx={{ height: 18, fontSize: 11 }}
      />
    </TableCell>,
  ];
}

function bankCells(row: MasterRecordRow) {
  const meta = row.metadata || {};
  const international = meta.internationalTransfers === true;
  const swift = String(meta.swiftCode || '');
  const prefix = String(meta.ifscPrefix || '');
  const usage = Number(row.usageCount) || 0;
  return [
    <TableCell key="ifscPrefix" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      <Typography variant="caption" fontFamily="monospace">{prefix || (row.country === 'IN' ? '—' : 'N/A')}</Typography>
    </TableCell>,
    <TableCell key="swift" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      {international ? (
        <Typography variant="caption" fontFamily="monospace">{swift || '—'}</Typography>
      ) : (
        <Tooltip title="Domestic only — this bank is not set up for international transfers, so SWIFT / BIC is not applicable">
          <Chip size="small" label="Domestic only" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
        </Tooltip>
      )}
    </TableCell>,
    <TableCell key="usage" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
      <Typography variant="caption" color="text.secondary">
        {usage ? `${usage} account${usage === 1 ? '' : 's'}` : 'Not used'}
      </Typography>
    </TableCell>,
  ];
}

function payoutCycleCells(row: MasterRecordRow) {
  const days = Number(row.metadata?.days) || 0;
  const partners = Number(row.partnerCount) || 0;
  return [
    <TableCell key="cycle" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography variant="caption">{days ? `Every ${days} day${days === 1 ? '' : 's'}` : '—'}</Typography>
        {row.isDefault && (
          <Tooltip title="Partners who haven't chosen a cycle (or chose an inactive one) are paid on this cycle — the first active cycle by sort order">
            <Chip size="small" color="primary" label="Default" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
          </Tooltip>
        )}
      </Stack>
    </TableCell>,
    <TableCell key="partners" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="caption" color="text.secondary">
        {partners ? `${partners} partner${partners === 1 ? '' : 's'}` : 'None chosen'}
      </Typography>
    </TableCell>,
  ];
}

function paymentMethodCells(row: MasterRecordRow) {
  const meta = row.metadata || {};
  const status = row.gatewayStatus;
  const fee = Number(meta.feePercent) || 0;
  const basis = String(meta.feeBasis || (fee ? '' : 'none'));
  return [
    <TableCell key="gateway" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      <Typography variant="body2" noWrap>{status?.label || String(meta.gateway || '—')}</Typography>
      <Tooltip title={status?.reason || (status?.kind === 'offline' ? 'Built-in gateway — no setup needed' : 'Gateway is enabled and has credentials')}>
        <Chip
          size="small"
          label={status?.ready ? 'Ready' : 'Not ready'}
          color={status?.ready ? 'success' : 'warning'}
          variant="outlined"
          sx={{ height: 18, fontSize: 11 }}
        />
      </Tooltip>
    </TableCell>,
    <TableCell key="fee" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="body2" fontFamily="monospace" noWrap>{fee ? `${fee}%` : '—'}</Typography>
      <Typography variant="caption" color={fee && !PAYMENT_FEE_BASIS_LABELS[basis] ? 'warning.main' : 'text.secondary'} noWrap display="block">
        {PAYMENT_FEE_BASIS_LABELS[basis] || 'Fee basis not set'}
      </Typography>
    </TableCell>,
  ];
}

function languageCells(row: MasterRecordRow) {
  const meta = row.metadata || {};
  const rtl = meta.rtl === true;
  const status = row.translationStatus || translationStatusOf(meta);
  const statusMeta = TRANSLATION_STATUS_META[status];
  const untranslated = Number(meta.untranslatedCount) || 0;
  return [
    <TableCell key="nativeName" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      <Typography variant="body2" dir={rtl ? 'rtl' : 'ltr'} noWrap>{String(meta.nativeName || '—')}</Typography>
    </TableCell>,
    <TableCell key="locale" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="caption" fontFamily="monospace">{String(meta.locale || '—')}</Typography>
    </TableCell>,
    <TableCell key="direction" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="caption">{rtl ? 'RTL' : 'LTR'}</Typography>
    </TableCell>,
    <TableCell key="translations" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      <Tooltip title={statusMeta.description}>
        <Chip
          size="small"
          label={status === 'failed' && untranslated ? `${untranslated} in English` : statusMeta.label}
          color={statusMeta.color}
          variant="outlined"
          sx={{ height: 20, fontSize: 11 }}
        />
      </Tooltip>
    </TableCell>,
  ];
}

function currencyCells(row: MasterRecordRow) {
  const meta = row.metadata || {};
  const code = row.code.toUpperCase();
  const fmt = formatFromMetadata(meta, code);
  const rate = Number(meta.exchangeRate);
  const base = row.baseCurrency || '';
  const auto = meta.autoUpdate === true;
  const cash = Number(meta.cashRounding);
  const min = Number(meta.minPayment) || 0;
  const max = Number(meta.maxPayment) || 0;
  const numeric = String(meta.isoNumeric ?? '').trim();
  return [
    <TableCell key="isoNumeric" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      <Typography variant="caption" fontFamily="monospace" color={numeric ? 'text.primary' : 'error.main'}>
        {numeric || 'Missing'}
      </Typography>
    </TableCell>,
    <TableCell key="format" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
      <Typography variant="body2" fontFamily="monospace" noWrap>{formatCurrencyAmount(1234.5, fmt)}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {fmt.symbol} · {fmt.decimals} dp · symbol {fmt.position}
      </Typography>
    </TableCell>,
    <TableCell key="rate" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
      {row.isBase ? (
        <Typography variant="caption" color="text.secondary">Base (1)</Typography>
      ) : (
        <>
          <Typography variant="body2" noWrap>{rate > 0 ? `1 ${base} = ${rate} ${code}` : '—'}</Typography>
          <Chip
            size="small"
            label={auto ? 'Auto · Frankfurter' : 'Manual'}
            color={auto ? 'success' : 'default'}
            variant="outlined"
            sx={{ height: 18, fontSize: 11 }}
          />
        </>
      )}
    </TableCell>,
    <TableCell key="rounding" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
      <Typography variant="body2" noWrap>{ROUNDING_LABELS[String(meta.roundingRule)] || 'Half up'}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {cash > 0 ? `Cash ${formatCurrencyAmount(cash, fmt)}` : 'No cash rounding'}
      </Typography>
    </TableCell>,
    <TableCell key="limits" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
      <Typography variant="body2" noWrap>
        {formatCurrencyAmount(min, fmt)} – {max > 0 ? formatCurrencyAmount(max, fmt) : 'No limit'}
      </Typography>
    </TableCell>,
  ];
}

interface Props {
  rows: MasterRecordRow[];
  schema?: MasterSchema | null;
  masterTypeKey?: string;
  loading?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (row: MasterRecordRow) => void;
  onDelete: (row: MasterRecordRow) => void;
  canDelete?: (row: MasterRecordRow) => boolean;
  /** Optional view / drill-down action (e.g. eye icon for sub-categories). */
  onView?: (row: MasterRecordRow) => void;
  viewTooltip?: string;
  onToggleActive?: (row: MasterRecordRow, active: boolean) => void;
  togglingRowId?: string | null;
  /** When true, status column shows only a switch (no Active/Inactive chip label). */
  statusToggleOnly?: boolean;
  /** Scroll rows inside the table so pagination stays visible on large lists. */
  stickyPagination?: boolean;
  emptyMessage?: string;
}

const headCellSx = {
  fontWeight: 650,
  fontSize: '0.68rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: 'action.hover',
  color: 'text.secondary',
  py: 1.35,
} as const;

function isImageMetaField(field: MasterFieldDef) {
  if (field.type === 'image') return true;
  return ['image', 'imageUrl', 'icon', 'iconUrl', 'iconImage', 'logo', 'banner', 'thumbnail', 'photo', 'coverImage', 'highlightImageUrl'].includes(field.key);
}

function isColorMetaField(field: MasterFieldDef) {
  return field.type === 'color' || field.key === 'color';
}

function normalizeTagHexColor(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw || !RIDE_TAG_COLOR_PATTERN.test(raw)) return '';
  return raw;
}

function MasterRecordColorSwatch({ value, fallbackHex }: { value: unknown; fallbackHex?: string }) {
  const hex = normalizeTagHexColor(value) || normalizeTagHexColor(fallbackHex);
  if (!hex) {
    return (
      <Typography variant="caption" color="text.secondary">—</Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        aria-hidden
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: hex,
          border: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      />
      <Typography variant="caption" fontFamily="monospace" color="text.secondary">
        {hex}
        {!normalizeTagHexColor(value) && fallbackHex ? ' (default)' : ''}
      </Typography>
    </Stack>
  );
}

function serviceCategoryAccentFallback(code: string, name: string): string {
  const palette = ['#0f766e', '#2563eb', '#7c3aed', '#c2410c', '#be123c', '#047857', '#1d4ed8'];
  const seed = `${code}|${name}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = ((hash << 5) - hash) + seed.charCodeAt(i);
  return palette[Math.abs(hash) % palette.length];
}

function serviceCategoryIconFallback(code: string, name: string, color?: string): string {
  const accent = (color && RIDE_TAG_COLOR_PATTERN.test(color) ? color : serviceCategoryAccentFallback(code, name));
  const initial = (name.replace(/\[deprecated\]/ig, '').trim()[0] || '?').toUpperCase();
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">`
    + `<rect width="96" height="96" rx="20" fill="${accent}"/>`
    + `<text x="48" y="58" text-anchor="middle" fill="#fff" font-family="Arial,sans-serif" font-size="40" font-weight="700">${initial}</text>`
    + `</svg>`,
  )}`;
}

function resolveMetaImageSrc(
  row: MasterRecordRow,
  field: MasterFieldDef,
  masterTypeKey: string,
): { primary: string; fallback: string } {
  if (masterTypeKey === 'food_categories' && field.key === 'imageUrl') {
    return resolveFoodCategoryListImageUrl(row.code, row.metadata);
  }
  if (masterTypeKey === 'cuisine_types' && field.key === 'imageUrl') {
    return resolveCuisineListImageUrl(row.code, row.metadata);
  }
  if (masterTypeKey === 'dietary_types' && field.key === 'icon') {
    return resolveDietaryListImageUrl(row.code, row.metadata);
  }
  if (masterTypeKey === 'food_images' && field.key === 'imageUrl') {
    return resolveFoodImageListImageUrl(row.metadata);
  }
  if (masterTypeKey === 'packaging_types' && field.key === 'imageUrl') {
    return resolvePackagingListImageUrl(row.code, row.metadata);
  }
  if (masterTypeKey === 'grocery_categories' && field.key === 'imageUrl') {
    return resolveGroceryCategoryListImageUrl(row.code, row.metadata);
  }
  if (masterTypeKey === 'grocery_sub_categories' && field.key === 'imageUrl') {
    return resolveGrocerySubCategoryListImageUrl(row.code, row.metadata);
  }
  if (masterTypeKey === 'grocery_product_images' && field.key === 'imageUrl') {
    return resolveGroceryProductListImageUrl(row.code, row.metadata);
  }
  if (masterTypeKey === 'service_categories' && (field.key === 'iconUrl' || field.key === 'icon' || field.key === 'imageUrl')) {
    const metaColor = String(row.metadata?.color ?? '').trim();
    const direct = row.metadata?.[field.key] ?? row.metadata?.iconUrl ?? row.metadata?.imageUrl;
    const primary = direct != null && String(direct).trim()
      ? resolveAssetUrl(String(direct))
      : serviceCategoryIconFallback(row.code, row.name, metaColor);
    return { primary, fallback: serviceCategoryIconFallback(row.code, row.name, metaColor) };
  }
  const direct = row.metadata?.[field.key];
  if (direct != null && String(direct).trim()) {
    const primary = resolveAssetUrl(String(direct));
    return { primary, fallback: '' };
  }
  if (isImageMetaField(field)) {
    const primary = resolveAssetUrl(resolveTransportMasterImageUrl(row.metadata));
    return { primary, fallback: '' };
  }
  return { primary: '', fallback: '' };
}

const masterImageBoxSx = {
  width: 96,
  height: 44,
  borderRadius: 1,
  border: 1,
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'action.hover',
  color: 'text.disabled',
  overflow: 'hidden',
} as const;

const masterIconBoxSx = {
  ...masterImageBoxSx,
  width: 44,
  height: 44,
} as const;

function MasterRecordImage({
  src,
  fallbackSrc,
  sources,
  alt,
  square,
  unavailableLabel = 'image unavailable',
}: {
  src?: string;
  fallbackSrc?: string;
  sources?: string[];
  alt: string;
  square?: boolean;
  unavailableLabel?: string;
}) {
  const sourceChain = useMemo(() => {
    const chain = sources?.length
      ? sources
      : [src, fallbackSrc].filter((value): value is string => !!value);
    return chain.filter((value, index) => value && chain.indexOf(value) === index);
  }, [sources, src, fallbackSrc]);

  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [sourceChain.join('|')]);

  const activeSrc = sourceChain[sourceIndex] || '';
  const exhausted = !activeSrc || sourceIndex >= sourceChain.length;

  const handleError = () => {
    if (sourceIndex + 1 < sourceChain.length) {
      setSourceIndex(sourceIndex + 1);
      return;
    }
    setSourceIndex(sourceChain.length);
  };

  if (exhausted) {
    return (
      <Box sx={square ? masterIconBoxSx : masterImageBoxSx} aria-label={`${alt} ${unavailableLabel}`}>
        <ImageOutlined sx={{ fontSize: 18 }} />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={activeSrc}
      alt=""
      referrerPolicy="no-referrer"
      onError={handleError}
      sx={{
        ...(square ? masterIconBoxSx : masterImageBoxSx),
        objectFit: square ? 'contain' : 'cover',
        display: 'block',
        p: square ? 0.25 : 0,
      }}
    />
  );
}

function formatMetaValue(
  field: MasterFieldDef,
  value: unknown,
  zoneMap: Map<string, string>,
  masterMaps: Map<string, Map<string, string>>,
  masterTypeKey: string,
  row?: MasterRecordRow,
): string {
  if (field.type === 'boolean' || field.key === 'required' || field.key === 'taxIncluded' || field.key === 'allowSubstitute') {
    if (masterTypeKey === 'merchant_document_types'
      && (field.key === 'required' || field.key === 'identifierRequired' || field.key === 'expiryRequired')) {
      return formatMerchantDocumentRuleFlag(field.key, value);
    }
    if (field.key === 'allowSubstitute') {
      return formatSubstitutionAllowDisplay(value);
    }
    if (value == null || value === '') return 'No';
    return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
  }
  if (field.key === 'complianceRules' && masterTypeKey === 'merchant_document_types') {
    return String(value ?? '').trim() || (row?.metadata ? formatMerchantDocumentComplianceRules(row.metadata) : '—');
  }
  if (field.key === 'category' && masterTypeKey === 'merchant_document_types') {
    return formatMerchantDocumentCategory(value);
  }
  if (field.key === 'entityTypes' && masterTypeKey === 'merchant_document_types') {
    return formatMerchantDocumentEntityTypes(value);
  }
  if (field.key === 'appliesTo' && masterTypeKey === 'cancellation_reasons') {
    return formatCancellationReasonAppliesTo(value, row?.metadata);
  }
  if (field.key === 'peakWindow' && masterTypeKey === 'delivery_incentives') {
    return String(value ?? '').trim() || (row?.metadata ? formatDeliveryIncentivePeakWindow(row.metadata) : '—');
  }
  if (field.key === 'incentiveType' && masterTypeKey === 'delivery_incentives') {
    const raw = String(value ?? '').trim();
    if (!raw) return '—';
    return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (value == null || value === '') {
    if (masterTypeKey === 'restaurant_opening_hours' && field.key === 'closeTime' && row?.metadata) {
      return formatOpeningHourTimeRange(row.metadata);
    }
    return '—';
  }
  if (field.key === 'day' && masterTypeKey === 'restaurant_opening_hours') {
    return formatOpeningHourDayLabel(value);
  }
  if (field.key === 'openTime' && masterTypeKey === 'restaurant_opening_hours' && row?.metadata) {
    return formatOpeningHourTimeRange(row.metadata);
  }
  if (field.key === 'closeTime' && masterTypeKey === 'restaurant_opening_hours') {
    return '';
  }
  if (field.key === 'daysOfWeek') {
    return formatAvailabilityDaysOfWeek(value);
  }
  if (field.key === 'sortOrder') {
    const order = row ? resolveRecordSortOrder(row) : Number(value);
    if (order > 0) return String(order);
    // Top-level sortOrder can be 0 for legacy rows — still surface it when present.
    const top = row && typeof row.sortOrder === 'number' && Number.isFinite(row.sortOrder)
      ? row.sortOrder
      : Number(value);
    if (Number.isFinite(top) && top >= 0 && String(value ?? row?.sortOrder ?? '') !== '') {
      return String(top);
    }
    return '—';
  }
  if (field.key === 'unitType' && masterTypeKey === 'food_units') {
    return formatFoodUnitTypeLabel(value) || '—';
  }
  if (field.key === 'stationType' && masterTypeKey === 'kitchen_stations') {
    return formatKitchenStationTypeLabel(value) || '—';
  }
  if (field.key === 'bonusPercent' && masterTypeKey === 'delivery_partners_tiers') {
    return formatDeliveryPartnerTierBonusDisplay(value);
  }
  if (field.key === 'taxPercent' || field.type === 'percent') {
    return formatTaxPercentDisplay(value);
  }
  if (field.key === 'fee' && masterTypeKey === 'packaging_types') {
    return formatPackagingFeeDisplay(value);
  }
  if (field.key === 'amount' && masterTypeKey === 'delivery_incentives') {
    return formatDeliveryIncentiveAmountDisplay(value);
  }
  if (field.key === 'serviceSlug' && (masterTypeKey === 'delivery_incentives' || masterTypeKey === 'delivery_partner_pricing')) {
    return formatDeliveryPartnerPricingServiceLabel(value) || '—';
  }
  if (field.key === 'minOrders' && masterTypeKey === 'delivery_partners_tiers') {
    return formatDeliveryPartnerTierMinOrdersDisplay(value);
  }
  if (field.type === 'currency' || field.key === 'price' || field.key === 'costPrice' || field.key === 'fee') {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return formatMoney(num);
  }
  if (field.key === 'zoneCode' || field.key.toLowerCase().includes('zone')) {
    const code = String(value);
    const name = zoneMap.get(code);
    return name ? `${name} (${code})` : code;
  }
  const refType = getMasterTypeForField(field, masterTypeKey);
  if (refType) {
    if (field.multiple) {
      const codes = parseCommaSeparatedMasterCodes(value);
      if (!codes.length) return '—';
      return codes.map((code) => {
        if (isParentCategoryRefField(masterTypeKey, field.key, refType)) {
          return formatParentCategoryRefLabel(masterMaps, refType, code);
        }
        const name = masterMaps.get(refType)?.get(code);
        return name ? `${name} (${code})` : code;
      }).join(', ');
    }
    const code = String(value);
    if (isParentCategoryRefField(masterTypeKey, field.key, refType)) {
      return formatParentCategoryRefLabel(masterMaps, refType, code);
    }
    const name = masterMaps.get(refType)?.get(code);
    return name ? `${name} (${code})` : code;
  }
  return String(value);
}

function useMasterRefMaps(metaFields: MasterFieldDef[], masterTypeKey: string) {
  const types = useMemo(() => {
    const set = new Set<string>();
    for (const f of metaFields) {
      const mt = getMasterTypeForField(f, masterTypeKey);
      if (mt) set.add(mt);
    }
    return [...set];
  }, [metaFields, masterTypeKey]);

  const results = useQueries({
    queries: types.map((t) => ({
      queryKey: ['master-options', t],
      queryFn: () => getMasterOptions(t),
      staleTime: 60_000,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    types.forEach((t, i) => {
      const codeMap = new Map<string, string>();
      (results[i]?.data || []).forEach((o) => {
        codeMap.set(o.code, o.name);
        codeMap.set(o.code.toUpperCase(), o.name);
        codeMap.set(o.code.toLowerCase(), o.name);
      });
      map.set(t, codeMap);
    });
    return map;
  }, [types, results]);
}

export default function MasterRecordTable({
  rows,
  schema,
  masterTypeKey = '',
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  canDelete,
  onView,
  viewTooltip = 'View',
  onToggleActive,
  togglingRowId,
  statusToggleOnly = false,
  stickyPagination = false,
  emptyMessage,
}: Props) {
  const theme = useTheme();
  const { t } = useI18n();
  const zoneMap = useZoneCodeMap();
  const tl = (s: string) => translateUiText(s, t);
  const showGeo = schema?.geoFields?.length;
  const metaFields = normalizeMetadataFields(schema?.metadataFields);
  const keyMetaFields = useMemo(() => {
    const fields = metaFields.filter((f) => f.key !== 'status' && f.key !== 'description');
    if (masterTypeKey === 'restaurant_opening_hours') {
      const dayField = fields.find((f) => f.key === 'day') || { key: 'day', label: 'Day', type: 'select' as const };
      const hoursField = fields.find((f) => f.key === 'openTime') || { key: 'openTime', label: 'Hours (24h)', type: 'time' as const };
      return [dayField, hoursField];
    }
    if (masterTypeKey === 'food_attribute_values') {
      return [
        {
          key: 'attributeCode',
          label: 'Parent Attribute',
          type: 'master' as const,
          masterType: 'food_attributes',
        },
        {
          key: 'sortOrder',
          label: 'Display Order',
          type: 'number' as const,
        },
      ];
    }
    if (masterTypeKey === 'food_sub_categories') {
      const parentField = fields.find((f) => f.key === 'parentCategoryCode') || {
        key: 'parentCategoryCode',
        label: 'Parent Category',
        type: 'master' as const,
        masterType: 'food_categories',
      };
      return [
        parentField,
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'food_variants') {
      return [
        { key: 'sku', label: 'SKU', type: 'text' as const },
        { key: 'price', label: chargeFieldLabel('Price'), type: 'currency' as const },
        { key: 'costPrice', label: chargeFieldLabel('Cost Price'), type: 'currency' as const },
      ];
    }
    if (masterTypeKey === 'addon_groups') {
      return [
        { key: 'minSelection', label: 'Min Selection', type: 'number' as const },
        { key: 'maxSelection', label: 'Max Selection', type: 'number' as const },
        { key: 'required', label: 'Required Group', type: 'boolean' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'food_addons') {
      return [
        { key: 'imageUrl', label: 'Addon Image', type: 'image' as const },
        { key: 'addonGroupCode', label: 'Addon Group', type: 'master' as const, masterType: 'addon_groups' },
        { key: 'price', label: chargeFieldLabel('Price'), type: 'currency' as const },
      ];
    }
    if (masterTypeKey === 'preparation_times') {
      return [
        { key: 'minutes', label: 'Minutes', type: 'number' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'food_units') {
      return [
        { key: 'unitType', label: 'Unit Type', type: 'text' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'food_tax_groups') {
      return [
        { key: 'taxPercent', label: 'Tax %', type: 'percent' as const },
        { key: 'taxIncluded', label: 'Tax Included', type: 'boolean' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'kitchen_stations') {
      return [
        { key: 'stationType', label: 'Station Type', type: 'text' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'food_availability_schedules') {
      return [
        { key: 'daysOfWeek', label: 'Days', type: 'select' as const, multiple: true },
        { key: 'startTime', label: 'Start Time', type: 'time' as const },
        { key: 'endTime', label: 'End Time', type: 'time' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'food_images') {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        { key: 'foodCode', label: 'Food Item Code', type: 'text' as const },
        { key: 'imageType', label: 'Image Type', type: 'select' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'delivery_slots') {
      return [
        { key: 'start', label: 'Start Time', type: 'time' as const },
        { key: 'end', label: 'End Time', type: 'time' as const },
        { key: 'fee', label: 'Slot Fee', type: 'currency' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'packaging_types') {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        { key: 'fee', label: 'Packaging Fee', type: 'currency' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'delivery_partner_pricing') {
      return [
        { key: 'zoneCode', label: 'Zone', type: 'zone' as const },
        { key: 'serviceSlug', label: 'Service', type: 'select' as const },
        { key: 'baseAmount', label: chargeFieldLabel('Base Amount'), type: 'currency' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'grocery_store_types') {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'grocery_brands') {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'warehouses') {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        { key: 'address', label: 'Address', type: 'text' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'product_variants') {
      return [
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'weight_types') {
      return [
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'suppliers') {
      return [
        { key: 'phone', label: 'Phone', type: 'text' as const },
        { key: 'email', label: 'Email', type: 'text' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'stock_alerts') {
      return [
        { key: 'threshold', label: 'Threshold', type: 'number' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'grocery_tax_groups') {
      return [
        { key: 'taxPercent', label: 'Tax %', type: 'percent' as const },
        { key: 'taxIncluded', label: 'Tax Included', type: 'boolean' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'grocery_product_images') {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        { key: 'skuCode', label: 'Product SKU', type: 'text' as const },
        { key: 'imageType', label: 'Image Type', type: 'select' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'grocery_availability_schedules') {
      return [
        { key: 'day', label: 'Day', type: 'select' as const },
        { key: 'startTime', label: 'Start Time', type: 'time' as const },
        { key: 'endTime', label: 'End Time', type: 'time' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'grocery_categories') {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'grocery_sub_categories') {
      const parentField = fields.find((f) => f.key === 'parentCategoryCode') || {
        key: 'parentCategoryCode',
        label: 'Parent Category',
        type: 'master' as const,
        masterType: 'grocery_categories',
      };
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        parentField,
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'delivery_incentives') {
      return [
        { key: 'incentiveType', label: 'Incentive Type', type: 'select' as const },
        { key: 'serviceSlug', label: 'Service', type: 'select' as const },
        { key: 'peakWindow', label: 'Peak Window', type: 'text' as const },
        { key: 'amount', label: chargeFieldLabel('Amount / order'), type: 'currency' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'delivery_partners_tiers') {
      return [
        { key: 'minOrders', label: 'Min Orders', type: 'number' as const },
        { key: 'bonusPercent', label: 'Bonus (% of earnings)', type: 'percent' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'merchant_document_types') {
      return [
        { key: 'category', label: 'Category', type: 'select' as const },
        { key: 'complianceRules', label: 'Compliance Rules', type: 'text' as const },
        { key: 'entityTypes', label: 'Applies To', type: 'text' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'cancellation_reasons') {
      return [
        { key: 'appliesTo', label: 'Applies To', type: 'select' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'substitution_rules') {
      return [
        { key: 'allowSubstitute', label: 'Allow Substitute', type: 'boolean' as const },
        { key: 'sortOrder', label: 'Display Order', type: 'number' as const },
      ];
    }
    if (masterTypeKey === 'states' || masterTypeKey === 'cities' || masterTypeKey === 'currencies' || masterTypeKey === 'languages' || masterTypeKey === 'timezones') {
      return [{ key: 'sortOrder', label: 'Sort Order', type: 'number' as const }];
    }
    if (masterTypeKey === 'address_fields' || masterTypeKey === 'payment_methods' || masterTypeKey === 'banks' || masterTypeKey === 'payout_cycles') {
      return [{ key: 'sortOrder', label: masterTypeKey === 'address_fields' ? 'Display Order' : 'Sort Order', type: 'number' as const }];
    }
    const imageFields = fields.filter(isImageMetaField);
    const otherFields = fields.filter((f) => !isImageMetaField(f));
    const merged = [...imageFields, ...otherFields];
    if (
      (VEHICLE_FLEET_MASTERS as readonly string[]).includes(masterTypeKey)
      && !imageFields.length
    ) {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        ...otherFields,
      ].slice(0, 3);
    }
    return merged.slice(0, 3);
  }, [metaFields, masterTypeKey]);
  const showStatusToggle = !!onToggleActive;
  const isAddonGroupsTable = masterTypeKey === 'addon_groups' || schema?.type === 'addon_groups';
  const showDescriptionColumn = isAddonGroupsTable;
  const masterMaps = useMasterRefMaps(keyMetaFields, masterTypeKey);
  const duplicateOpeningDays = useMemo(
    () => (masterTypeKey === 'restaurant_opening_hours' ? findDuplicateOpeningHourDays(rows) : []),
    [masterTypeKey, rows],
  );

  const frameSx = {
    borderRadius: 2,
    border: `1px solid ${alpha('#0f172a', 0.08)}`,
    overflow: 'hidden',
    bgcolor: 'background.paper',
  } as const;

  if (loading) {
    return (
      <Paper elevation={0} sx={frameSx}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={44} sx={{ mx: 2, my: 0.5, borderRadius: 1 }} />
        ))}
      </Paper>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          ...frameSx,
          borderStyle: 'dashed',
          py: 7,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Inbox sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          {tl(emptyMessage || 'No records yet')}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
          {tl(emptyMessage ? 'Change the country, status or search to see more' : 'Click Add to create the first entry')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={frameSx}>
      {duplicateOpeningDays.length > 0 && (
        <Box sx={{ px: 2, py: 1.25, bgcolor: alpha(theme.palette.warning.main, 0.08), borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="warning.dark" fontWeight={600}>
            Duplicate active days detected: {duplicateOpeningDays.map(formatOpeningHourDayLabel).join(', ')}. Only one record per day is allowed — edit or deactivate duplicates.
          </Typography>
        </Box>
      )}
      <TableContainer sx={stickyPagination ? { maxHeight: 'calc(100vh - 340px)', minHeight: 260 } : undefined}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {[
                <TableCell key="#" sx={{ ...headCellSx, width: 52 }}>#</TableCell>,
                <TableCell key="name" sx={headCellSx}>{tl('Name')}</TableCell>,
                ...(showDescriptionColumn
                  ? [
                      <TableCell key="description" sx={{ ...headCellSx, minWidth: 160 }}>
                        {tl('Description')}
                      </TableCell>,
                    ]
                  : []),
                <TableCell key="code" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('Code')}</TableCell>,
                ...(showGeo && schema?.geoFields?.includes('country')
                  ? [<TableCell key="country" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Country')}</TableCell>]
                  : []),
                ...(masterTypeKey === 'cities'
                  ? [<TableCell key="state" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('State / Province')}</TableCell>]
                  : []),
                ...(masterTypeKey === 'currencies'
                  ? [
                      <TableCell key="isoNumeric" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('ISO Numeric')}</TableCell>,
                      <TableCell key="format" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('Format')}</TableCell>,
                      <TableCell key="rate" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Exchange Rate')}</TableCell>,
                      <TableCell key="rounding" sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>{tl('Rounding')}</TableCell>,
                      <TableCell key="limits" sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>{tl('Payment Limits')}</TableCell>,
                    ]
                  : []),
                ...(masterTypeKey === 'languages'
                  ? [
                      <TableCell key="nativeName" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('Native Name')}</TableCell>,
                      <TableCell key="locale" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Locale')}</TableCell>,
                      <TableCell key="direction" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Direction')}</TableCell>,
                      <TableCell key="translations" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('Translations')}</TableCell>,
                    ]
                  : []),
                ...(masterTypeKey === 'timezones'
                  ? [
                      <TableCell key="ianaId" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('IANA ID')}</TableCell>,
                      <TableCell key="offset" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('UTC Offset')}</TableCell>,
                      <TableCell key="now" sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>{tl('Offset Now')}</TableCell>,
                      <TableCell key="abbreviation" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Abbreviation')}</TableCell>,
                      <TableCell key="countries" sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>{tl('Countries')}</TableCell>,
                    ]
                  : []),
                ...(masterTypeKey === 'address_fields'
                  ? [
                      <TableCell key="fieldKey" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('Field Key')}</TableCell>,
                      <TableCell key="geoLevel" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Geo Level')}</TableCell>,
                      <TableCell key="fieldType" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Field Type')}</TableCell>,
                      <TableCell key="source" sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>{tl('Data Source')}</TableCell>,
                      <TableCell key="required" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Required')}</TableCell>,
                    ]
                  : []),
                ...(masterTypeKey === 'payment_methods'
                  ? [
                      <TableCell key="gateway" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('Gateway')}</TableCell>,
                      <TableCell key="fee" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Fee')}</TableCell>,
                    ]
                  : []),
                ...(masterTypeKey === 'banks'
                  ? [
                      <TableCell key="ifscPrefix" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('IFSC Prefix')}</TableCell>,
                      <TableCell key="swift" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('SWIFT / BIC')}</TableCell>,
                      <TableCell key="usage" sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>{tl('Used By')}</TableCell>,
                    ]
                  : []),
                ...(masterTypeKey === 'payout_cycles'
                  ? [
                      <TableCell key="cycle" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('Cycle')}</TableCell>,
                      <TableCell key="partners" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Partners')}</TableCell>,
                    ]
                  : []),
                ...keyMetaFields.map((f) => (
                  <TableCell
                    key={f.key}
                    sx={{
                      ...headCellSx,
                      display: { xs: 'none', [isImageMetaField(f) || isColorMetaField(f) ? 'sm' : f.key === 'sortOrder' ? 'md' : 'lg']: 'table-cell' },
                    }}
                  >
                    {tl(f.label)}
                  </TableCell>
                )),
                <TableCell key="status" sx={{ ...headCellSx, width: showStatusToggle ? 120 : 72 }}>
                  {tl('Status')}
                </TableCell>,
                <TableCell key="actions" sx={{ ...headCellSx, width: onView ? 108 : 72 }} align="right">{tl('Actions')}</TableCell>,
              ]}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const deprecated = isMasterRecordDeprecated(row);
              const active = resolveMasterRecordActive(row);
              const toggleLockReason = masterTypeKey === 'languages' && !!row.isDefault && active
                ? 'Default language — choose another default in Locale settings before deactivating'
                : row.toggleLock || null;
              const defaultLocked = !!toggleLockReason;
              const serialNo = page * pageSize + index + 1;
              return (
                <TableRow
                  key={row._id}
                  hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                  }}
                  onClick={() => onEdit(row)}
                >
                  {[
                    <TableCell key="#">
                      <Typography variant="body2" fontWeight={600} color="text.secondary">{serialNo}</Typography>
                    </TableCell>,
                    <TableCell key="name">
                      <Typography variant="body2" fontWeight={700}>
                        {masterTypeKey === 'food_tax_groups' || masterTypeKey === 'grocery_tax_groups'
                          ? resolveFoodTaxGroupDisplayName(row)
                          : row.name}
                        {masterTypeKey === 'currencies' && row.isBase && (
                          <Chip size="small" label={tl('Base')} color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }} />
                        )}
                        {masterTypeKey === 'languages' && row.isDefault && (
                          <Chip size="small" label={tl('Default')} color="primary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }} />
                        )}
                        {masterTypeKey === 'address_fields' && row.isCore && (
                          <Chip size="small" label={tl('Core')} color="secondary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }} />
                        )}
                      </Typography>
                      {isOpeningHourRowInvalid(row.metadata) && (
                        <Typography variant="caption" color="error.main" display="block">
                          Closing time must be after opening time
                        </Typography>
                      )}
                      {masterTypeKey === 'delivery_slots' && isDeliverySlotTimeRangeInvalid(row.metadata) && (
                        <Typography variant="caption" color="error.main" display="block">
                          End time must be after start time
                        </Typography>
                      )}
                      {masterTypeKey === 'grocery_availability_schedules' && isGroceryAvailabilityTimeRangeInvalid(row.metadata) && (
                        <Typography variant="caption" color="error.main" display="block">
                          End time must be after start time
                        </Typography>
                      )}
                      {row.description && !showDescriptionColumn ? (
                        <Typography variant="caption" color={deprecated ? 'warning.main' : 'text.secondary'} noWrap sx={{ maxWidth: 240, display: 'block' }}>
                          {row.description}
                        </Typography>
                      ) : null}
                    </TableCell>,
                    ...(showDescriptionColumn
                      ? [
                          <TableCell key="description">
                            <Typography
                              variant="body2"
                              color={deprecated ? 'warning.main' : 'text.secondary'}
                              sx={{ maxWidth: 280, display: 'block' }}
                            >
                              {String(row.description ?? '').trim() || '—'}
                            </Typography>
                          </TableCell>,
                        ]
                      : []),
                    <TableCell key="code" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                        {masterTypeKey === 'food_addons' ? row.code.trim().toLowerCase() : row.code}
                      </Typography>
                    </TableCell>,
                    ...(showGeo && schema?.geoFields?.includes('country')
                      ? [
                          <TableCell key="country" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="caption">
                              {(() => {
                                // Grocery warehouses are India-scoped; legacy seed rows may omit country.
                                const countryCode = String(row.country || '').trim()
                                  || (masterTypeKey === 'warehouses' ? 'IN' : '');
                                return countryCode ? formatCountryCell(countryCode) : '—';
                              })()}
                            </Typography>
                          </TableCell>,
                        ]
                      : []),
                    ...(masterTypeKey === 'cities'
                      ? [
                          <TableCell key="state" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="caption">
                              {row.stateName
                                ? `${row.stateName} (${row.state})`
                                : row.state || '—'}
                            </Typography>
                          </TableCell>,
                        ]
                      : []),
                    ...(masterTypeKey === 'currencies' ? currencyCells(row) : []),
                    ...(masterTypeKey === 'languages' ? languageCells(row) : []),
                    ...(masterTypeKey === 'timezones' ? timezoneCells(row) : []),
                    ...(masterTypeKey === 'address_fields' ? addressFieldCells(row) : []),
                    ...(masterTypeKey === 'payment_methods' ? paymentMethodCells(row) : []),
                    ...(masterTypeKey === 'banks' ? bankCells(row) : []),
                    ...(masterTypeKey === 'payout_cycles' ? payoutCycleCells(row) : []),
                    ...keyMetaFields.map((f) => {
                      const raw = f.key === 'sortOrder'
                        ? resolveRecordSortOrder(row)
                        : f.key === 'daysOfWeek' && masterTypeKey === 'food_availability_schedules'
                          ? (row.metadata?.daysOfWeek ?? row.metadata?.day)
                          : f.key === 'peakWindow' && masterTypeKey === 'delivery_incentives'
                            ? formatDeliveryIncentivePeakWindow(row.metadata)
                          : f.key === 'complianceRules' && masterTypeKey === 'merchant_document_types'
                            ? formatMerchantDocumentComplianceRules(row.metadata)
                          : f.key === 'appliesTo' && masterTypeKey === 'cancellation_reasons'
                            ? (row.metadata?.appliesTo ?? resolveCancellationReasonAppliesTo(row.metadata))
                          : row.metadata?.[f.key];
                      if (isImageMetaField(f)) {
                        const isFoodAddonImage = masterTypeKey === 'food_addons' && f.key === 'imageUrl';
                        const isFoodMasterImage = masterTypeKey === 'food_images' && f.key === 'imageUrl';
                        const isPackagingImage = masterTypeKey === 'packaging_types' && f.key === 'imageUrl';
                        const isGroceryCategoryImage = masterTypeKey === 'grocery_categories' && f.key === 'imageUrl';
                        const isGrocerySubCategoryImage = masterTypeKey === 'grocery_sub_categories' && f.key === 'imageUrl';
                        const isGroceryProductImage = masterTypeKey === 'grocery_product_images' && f.key === 'imageUrl';
                        const { primary, fallback } = (isFoodAddonImage || isFoodMasterImage || isPackagingImage || isGroceryCategoryImage || isGrocerySubCategoryImage || isGroceryProductImage)
                          ? { primary: '', fallback: '' }
                          : resolveMetaImageSrc(row, f, masterTypeKey);
                        return (
                          <TableCell key={f.key} sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <MasterRecordImage
                              src={primary}
                              fallbackSrc={fallback || undefined}
                              sources={isFoodAddonImage
                                ? resolveFoodAddonImageSources(row.code, row.metadata)
                                : isFoodMasterImage
                                  ? resolveFoodImageSources(row.metadata)
                                  : isPackagingImage
                                    ? resolvePackagingImageSources(row.code, row.metadata)
                                    : isGroceryCategoryImage
                                      ? resolveGroceryCategoryImageSources(row.code, row.metadata)
                                      : isGrocerySubCategoryImage
                                        ? resolveGrocerySubCategoryImageSources(row.code, row.metadata)
                                      : isGroceryProductImage
                                        ? resolveGroceryProductImageSources(row.code, row.metadata)
                                      : undefined}
                              alt={row.name}
                              square={f.key === 'icon' || f.key === 'iconUrl' || isFoodAddonImage || isFoodMasterImage || isPackagingImage || isGroceryCategoryImage || isGrocerySubCategoryImage || isGroceryProductImage || masterTypeKey === 'service_categories'}
                              unavailableLabel={
                                isFoodAddonImage
                                  ? 'addon image unavailable'
                                  : isFoodMasterImage
                                    ? 'food image unavailable'
                                    : isPackagingImage
                                      ? 'packaging image unavailable'
                                      : isGroceryCategoryImage || isGrocerySubCategoryImage
                                        ? 'category image unavailable'
                                        : isGroceryProductImage
                                          ? 'product image unavailable'
                                        : masterTypeKey === 'service_categories'
                                          ? 'category icon unavailable'
                                        : 'image unavailable'
                              }
                            />
                          </TableCell>
                        );
                      }
                      if (isColorMetaField(f)) {
                        return (
                          <TableCell key={f.key} sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <MasterRecordColorSwatch
                              value={raw}
                              fallbackHex={
                                masterTypeKey === 'service_categories'
                                  ? serviceCategoryAccentFallback(row.code, row.name)
                                  : undefined
                              }
                            />
                          </TableCell>
                        );
                      }
                      const displayValue = formatMetaValue(f, raw, zoneMap, masterMaps, masterTypeKey, row);
                      const refType = getMasterTypeForField(f, masterTypeKey);
                      const isOrphanRef = !!refType
                        && f.key === 'addonGroupCode'
                        && raw != null
                        && String(raw).trim() !== ''
                        && displayValue === String(raw).trim();
                      return (
                        <TableCell
                          key={f.key}
                          sx={{ display: { xs: 'none', [f.key === 'sortOrder' ? 'md' : 'lg']: 'table-cell' } }}
                        >
                          <Typography
                            variant="caption"
                            color={isOrphanRef ? 'warning.main' : 'text.secondary'}
                            noWrap
                            title={isOrphanRef ? 'Addon group not found — check mapping' : undefined}
                          >
                            {displayValue}
                          </Typography>
                        </TableCell>
                      );
                    }),
                    <TableCell key="status" onClick={(e) => e.stopPropagation()}>
                      {showStatusToggle ? (
                        statusToggleOnly ? (
                          <Tooltip title={
                            deprecated
                              ? 'Deprecated — status toggle is locked (cannot reactivate)'
                              : toggleLockReason
                                ? toggleLockReason
                                : (active ? 'Active — click to deactivate' : 'Inactive — click to activate')
                          }>
                            <span>
                              <Switch
                                size="small"
                                checked={active}
                                disabled={deprecated || defaultLocked || togglingRowId === row._id}
                                onChange={(ev) => onToggleActive?.(row, ev.target.checked)}
                                inputProps={{ 'aria-label': deprecated
                                  ? `Deprecated — ${row.name} cannot be reactivated`
                                  : `${active ? 'Deactivate' : 'Activate'} ${row.name}` }}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Chip
                              label={deprecated ? 'Deprecated' : (active ? 'Active' : 'Inactive')}
                              size="small"
                              color={deprecated ? 'warning' : (active ? 'success' : 'default')}
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}
                            />
                            <Tooltip title={
                              deprecated
                                ? 'Deprecated — status toggle is locked (cannot reactivate)'
                                : toggleLockReason
                                  ? toggleLockReason
                                  : (active ? 'Active — click to deactivate' : 'Inactive — click to activate')
                            }>
                              <span>
                                <Switch
                                  size="small"
                                  checked={active}
                                  disabled={deprecated || defaultLocked || togglingRowId === row._id}
                                  onChange={(ev) => onToggleActive?.(row, ev.target.checked)}
                                  inputProps={{ 'aria-label': deprecated
                                    ? `Deprecated — ${row.name} cannot be reactivated`
                                    : `${active ? 'Deactivate' : 'Activate'} ${row.name}` }}
                                />
                              </span>
                            </Tooltip>
                          </Stack>
                        )
                      ) : (
                        <Chip
                          label={deprecated ? 'Deprecated' : (active ? 'Active' : 'Inactive')}
                          size="small"
                          color={deprecated ? 'warning' : (active ? 'success' : 'default')}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}
                        />
                      )}
                    </TableCell>,
                    <TableCell key="actions" align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" justifyContent="flex-end" spacing={0}>
                        {onView && (
                          <Tooltip title={viewTooltip}>
                            <IconButton size="small" onClick={() => onView(row)} aria-label={viewTooltip}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={deprecated ? 'Deprecated records can be deleted but not reactivated' : 'Edit'}>
                          <span>
                            <IconButton size="small" onClick={() => onEdit(row)}><Edit fontSize="small" /></IconButton>
                          </span>
                        </Tooltip>
                        {canDelete && !canDelete(row) && row.deleteLock && (
                          <Tooltip title={row.deleteLock}>
                            <span>
                              <IconButton size="small" disabled aria-label={row.deleteLock}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {(!canDelete || canDelete(row)) && (
                          <Tooltip title={masterTypeKey === 'merchant_document_types' ? 'Deactivate' : 'Delete'}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDelete(row)}
                              aria-label={masterTypeKey === 'merchant_document_types' ? 'Deactivate' : 'Delete'}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>,
                  ]}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, p) => onPageChange(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={stickyPagination ? [10, 25, 50, 100] : [10, 25, 50]}
        showFirstButton={stickyPagination}
        showLastButton={stickyPagination}
        sx={{ borderTop: 1, borderColor: 'divider' }}
      />
    </Paper>
  );
}
